"use client"
import { getT } from "@local/i18n/src/t"
import { getLanguageForMarket } from "@local/i18n/src/languages"
import { formatDate } from "@local/i18n/src/format"
import { buy } from "@local/i18n/src/catalogs/buy"
import { isBrowser } from "@local/utils/src/isBrowser"
import { startTransition, useEffect, useState } from "react"

interface Props {
  action?: string
  input?: string | null
  truncate?: boolean
  discount?: number
  market?: string
}

// Replace discount percentage in CTA string
// Supports both %%DISCOUNT%% placeholder and hardcoded percentages like "-60%" or "60% OFF"
const replaceDiscount = (text: string, discount?: number): string => {
  if (!discount) return text

  // Replace %%DISCOUNT%% placeholder
  let result = text.replace("%%DISCOUNT%%", discount.toString())

  // Replace hardcoded percentages like "-60%" or "60% OFF"
  // Match patterns: -60%, 60% (with optional preceding dash/hyphen)
  result = result.replace(/[-–]?\d+%/g, (match) => {
    const hasDash = match.startsWith("-") || match.startsWith("–")
    return hasDash ? `-${discount}%` : `${discount}%`
  })

  return result
}

// Server-render fallback. Localized when the caller passes `market` — the
// wall-clock date itself still only materializes client-side (see below).
export const getInitialCtaString = (
  input?: string | null,
  action?: string,
  discount?: number,
  market?: string
) => {
  const t = getT(getLanguageForMarket(market), { buy })
  if (!input) return action || t("buy.buyNow")

  const result = input
    .replace("%%DATE%%", t("buy.today"))
    .replace("%%ACTION%%", action || t("buy.buyNow"))

  return replaceDiscount(result, discount)
}

export const CallToAction = ({
  action,
  input,
  truncate,
  discount,
  market,
}: Props) => {
  //  const ctaText = callToAction && callToAction !== "" ? callToAction :  "Buy now"

  const t = getT(getLanguageForMarket(market), { buy })
  const [callToAction, setCallToAction] = useState<string>(
    getInitialCtaString(input, action, discount, market)
  )

  useEffect(() => {
    // Defined inside the effect (rather than deps'd via a component-scope
    // function) so the dep array alone controls when this re-parses, not
    // every render of every CTA on the page.
    const parseCallToAction = () => {
      if (isBrowser() && input && input.indexOf("%%DATE") > -1) {
        // Locale-correct short date (e.g. "9 Jul" en, "9. Juli" de) —
        // replaces the hardcoded `en-US` that showed US month order on the
        // German LP.
        const date = formatDate(new Date(), getLanguageForMarket(market), {
          day: "numeric",
          month: "short",
        })

        const data = input
          .replace("%%DATE%%", date)
          .replace("%%ACTION%%", action || t("buy.selectSizeNow"))

        return replaceDiscount(data, discount)
      } else {
        const text = input ?? t("buy.buyNow")
        const result = text
          .replace("%%DATE%%", t("buy.today"))
          .replace("%%ACTION%%", action || t("buy.buyNow"))

        return replaceDiscount(result, discount)
      }
    }

    startTransition(() => {
      setCallToAction(parseCallToAction())
    })
  }, [input, action, discount, market, t])

  return (
    <span
      className={
        truncate
          ? "block w-full overflow-hidden text-ellipsis whitespace-nowrap md:whitespace-normal"
          : "block leading-5"
      }
    >
      {callToAction}
    </span>
  )
}

export default CallToAction
